import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './AgentOrb.scss';

/*
  The agent identity orb: a breathing gradient sphere with a slow internal
  energy swirl and a satellite navigating its rim. Each agent carries its own
  color profile — art-directed when an entry exists below, derived from the
  key otherwise, so a brand-new agent arrives with an identity for free.
  Pure CSS; every layer goes still under prefers-reduced-motion.
*/

// Art-direct an agent here ([core, light, deep]); any key absent derives a
// stable palette from its name below. The only agent this app renders today
// (personal-shopper) uses the derived palette.
const ORB_PALETTES = {};

const paletteForKey = agentKey => {
  if (ORB_PALETTES[agentKey]) return ORB_PALETTES[agentKey];
  let hue = 0;
  for (const char of String(agentKey || 'agent')) hue = (hue * 31 + char.charCodeAt(0)) % 360;
  return [`hsl(${hue}, 46%, 36%)`, `hsl(${(hue + 26) % 360}, 64%, 76%)`, `hsl(${hue}, 52%, 16%)`];
};

// The agent's core color — used to tint surfaces (e.g. the specialist profile hero) so they
// pick up the same identity the orb carries.
export const getAgentAccent = agentKey => paletteForKey(agentKey)[0];

// `quiet` drops the swirl and satellite — below ~20px they read as noise, so the
// small in-chat presence is just the breathing sphere. `thinking` adds the active
// state: a soft halo pulsing outward + a stronger breathe, legible even at 14-16px
// (where quiet has stripped the swirl), so "the agent is working" reads at a glance.
const AgentOrb = ({ agentKey, size = 40, quiet = false, thinking = false, className }) => {
  const [core, light, deep] = paletteForKey(agentKey);
  return (
    <span
      aria-hidden='true'
      className={cn('ap-agent-orb', { 'is-thinking': thinking }, className)}
      style={{ '--orb-size': `${size}px`, '--orb-core': core, '--orb-light': light, '--orb-deep': deep }}
    >
      <span className='ap-agent-orb-haze' />
      {thinking && <span className='ap-agent-orb-pulse' />}
      <span className='ap-agent-orb-sphere' />
      {!quiet && <span className='ap-agent-orb-swirl' />}
      {!quiet && (
        <span className='ap-agent-orb-ring'>
          <span className='ap-agent-orb-satellite' />
        </span>
      )}
    </span>
  );
};

AgentOrb.propTypes = {
  agentKey: PropTypes.string,
  size: PropTypes.number,
  quiet: PropTypes.bool,
  thinking: PropTypes.bool,
  className: PropTypes.string
};

export default AgentOrb;
